import { observable, action, computed } from 'mobx';
import _ from 'lodash';
import numeral from 'numeral';
import $ from 'jquery';
import 'numeral/locales/it';
import axios from 'axios';
import { postCache, getCache } from "../helpers/cache";

numeral.locale('it');

/* Default form used in overview */
const overviewForm = {
  nhg: 0,
  mortgage: 330000,
  houseValue: 330000,
  mortgageType: 6,
};


const allFixedRateTerms = Array(30).fill().map((item, index) => (index + 1));

class OverviewStore {
  constructor() {
    this.calculateInterestSavings = this.calculateInterestSavings.bind(this);
    this.sortLabelInterestsByInitialInterest = this.sortLabelInterestsByInitialInterest.bind(this);
    this.sortLabelInterestsByTermInterest = this.sortLabelInterestsByTermInterest.bind(this);
    this.sortLabelInterestsByTermInterestAvailable = this.sortLabelInterestsByTermInterestAvailable.bind(this);
    this.sortLabelInterests = this.sortLabelInterests.bind(this);
    this.getFixedRateTermIndex = this.getFixedRateTermIndex.bind(this);
    this.toggleShowAllProviders = this.toggleShowAllProviders.bind(this);
    this.getInterestForFrt = this.getInterestForFrt.bind(this);
    this.equalizeLabelInterests = this.equalizeLabelInterests.bind(this);
    this.addNewLabelInterests = this.addNewLabelInterests.bind(this);
    this.toggleNHG = this.toggleNHG.bind(this);
    this.calculateLoanparts = this.calculateLoanparts.bind(this);
    this.setOverviewForm = this.setOverviewForm.bind(this);
    this.setBaseFormData = this.setBaseFormData.bind(this);
    this.getInterestAndSortTable = _.debounce(this.getInterestAndSortTable.bind(this), 2000);
    this.cancelRequest = this.cancelRequest.bind(this);
    this.setInterestPaymentComparisonLabels = this.setInterestPaymentComparisonLabels.bind(this);
    this.getOverviewInterestPaymentComparisonData = this.getOverviewInterestPaymentComparisonData.bind(this);
    this.getComparisonDataWithDebounce = _.debounce(this.getComparisonDataWithDebounce.bind(this), 3000);
    this.sortInterestPaymentComparisonData = this.sortInterestPaymentComparisonData.bind(this);
    this.setOverviewInterestPaymentComparisonData = this.setOverviewInterestPaymentComparisonData.bind(this);
    this.setOpenSortingDropdown = this.setOpenSortingDropdown.bind(this);
    this.closeOpenSortingDropdown = this.closeOpenSortingDropdown.bind(this);
    this.setFirstOverviewTableColumnFixedRateTerm = this.setFirstOverviewTableColumnFixedRateTerm.bind(this);
    this.setSecondOverviewTableColumnFixedRateTerm = this.setSecondOverviewTableColumnFixedRateTerm.bind(this);
    this.getNewLabelInterests = this.getNewLabelInterests.bind(this);
  }

  @observable accessor firstOverviewTableColumnFixedRateTerm = 10;
  @observable accessor secondOverviewTableColumnFixedRateTerm = 20;

  @observable accessor openSortingDropdown = '';

  /* Setup data for use in OverviewInterestPaymentComparisonComponent */
  @observable accessor termInterestSavingsSeries = [{
    name: 'korting',
    data: [0, 0],
  }, {
    name: 'rente',
    data: [0, 0],
  }];

  @observable accessor dataIsEmpty = false;

  /* Default show 10 providers */
  @observable accessor showAllProviders = false;

  @observable accessor labelInterestsLoading = false;
  @observable accessor overviewInterestsErrorMessage = '';

  @observable accessor overviewForm = overviewForm;
  /* Setup table with 10 labels. Hypotrust, ASR and Centraal beheer on top */
  @observable accessor labelInterests = [];

  @observable accessor sortedOverviewTableColumn = 'initialInterest-10';

  /* Temporary set data from static file overviewBarChartData */
  @observable accessor overviewInterestPaymentComparisonData = {
    extraAmountForCategory: [],
    interests: [],
    categories: [],
    baseAmount: '',
  };

  /* Calculate one loanpart for InterestPaymentComparisonComponent */
  @action
  async calculateLoanparts(labelId) {
    const cacheMissCallback = async () => {
      const response = await axios.post(
        `/calculate/loanparts/cached?${labelId}`,
        {
          labelId,
          houseValue: this.overviewForm.houseValue,
          mortgage: this.overviewForm.mortgage,
          nhg: this.overviewForm.nhg,
          loanparts: [{
            gross: this.overviewForm.mortgage,
            frt: 20,
            loanpartDuration: '360',
            mortgageType: this.overviewForm.mortgageType,
          }],
        },
      );

      return response;
    };

    let res = {};
    if (
      this.overviewForm.houseValue == 330000
      && this.overviewForm.mortgage == 330000
      && this.overviewForm.nhg == 0
      && this.overviewForm.mortgageType == 6
    ) {
      res = postCache(labelId, cacheMissCallback);
    } else {
      res = cacheMissCallback();
    }

    return res;
  }

  /* Get interest for one frt */
  @action
  // eslint-disable-next-line consistent-return
  async getInterestForFrt(frtForRequest) {
    /* When api is ready for receiving post requests, add postData */
    const getData = this.setBaseFormData();
    if (!_.isEmpty(getData)) {
      this.labelInterestsLoading = true;
      const cacheMissCallback = async () => {
        const res = await axios.get(`/overview/interest?${$.param(getData)}&frt=${frtForRequest}`)
          .catch(err => this.errorMessage = err);

        return res;
      };

      const response = await getCache(`overview/interest?${$.param(getData)}&frt=${frtForRequest}`, cacheMissCallback);

      if (_.get(response, 'data.length') === 0) {
        this.overviewInterestsErrorMessage = 'Er zijn geen rentes gevonden.';
      } else {
        this.overviewInterestsErrorMessage = '';
      }

      return response.data;
    }
  }

  /* Helper method for getInterestForFrt */
  @action equalizeLabelInterests(labels) {
    return labels.map(this.equalizeSingleRow);
  }

  @action equalizeSingleRow(label) {

    const labelInterests = allFixedRateTerms.map((fixedRateTerm) => {
      if (_.some(label.interests, interest => interest.fixedRateTermDuration === fixedRateTerm)) {
        return label.interests.find(interest => interest.fixedRateTermDuration === fixedRateTerm);
      }

      return { fixedRateTerm, initialInterest: 0, averageInterest: 0 };
    });

    return { ...label, interests: labelInterests };
  }

  /* Helper method for getInterestForFrt */
  @action addNewLabelInterests(labelInterests, res) {
    let allLabels = labelInterests.map((label) => {
      /* Find label with the same id */
      const newInterestObject = res.find(newLabel => newLabel.id === label.id);

      /* Then find interest with the samen frt as the newInterestObject */
      const newInterests = label.interests.map((interest) => {
        /* Loop over each frt in interests and return equal frt  */
        if (newInterestObject && newInterestObject.interests &&
          newInterestObject.interests[0].fixedRateTermDuration === interest.fixedRateTerm) {
          return newInterestObject.interests[0];
        }

        return interest;
      });
      return { ...label, interests: newInterests };
    });

    const notSeenYet = res.filter(incoming => {
      return ! allLabels.some(knownLabel => knownLabel.id === incoming.id);
    }).map(this.equalizeSingleRow);

    return allLabels.concat(notSeenYet);
  }

  /* Calculate savings with and without looptijdrente for the first graph */
  @action
  async calculateInterestSavings() {
    const labelHasAutoRiskClassReduction = this.labelInterests.find(label => label.hasAutoRiskClassReduction);

    if (labelHasAutoRiskClassReduction) {
      const cacheMissCallback = async () => {
        const res = await axios.post('/calculate/interest-savings', {
          labelId: labelHasAutoRiskClassReduction.id,
          mortgage: 330000,
          houseValue: 330000,
          nhg: false,
          loanparts: [{
            gross: 330000,
            frt: 20,
            loanpartDuration: '360',
            mortgageType: '6',
          }],
        }).catch(err => console.log(err));

        return res;
      };

      const response = await getCache('calculate/interest-savings', cacheMissCallback);

      const withLooptijd = Math.floor(response.data.totalInterestWithLooptijdRente);
      const withoutLooptijd = Math.floor(response.data.totalInterestWithoutLooptijdRente);
      this.termInterestSavingsSeries[0].data = [0, withoutLooptijd - withLooptijd];
      this.termInterestSavingsSeries[1].data = [withoutLooptijd, withLooptijd];
    }
  }

  @action getOverviewInterestPaymentComparisonData(frt) {
    const filteredLabels = this.setInterestPaymentComparisonLabels(7, frt);

    /* Clear interests so OverviewInterestPaymentComparisonComponent will rerender */
    this.overviewInterestPaymentComparisonData.interests = [];

    const promises = filteredLabels
      .map(label => this.calculateLoanparts(label).then(res => res));

    Promise.all(promises)
      .then((results) => {
        const resultsWithLabels = results.map((label, index) => ({
          ...label,
          labelId: filteredLabels[index],
        }));


        if (!this.checkempty(results)) {
          const sortedResults = this.sortInterestPaymentComparisonData(results);
          const baseAmount = sortedResults[0];
          const extraAmountForCategory = sortedResults.map(result => Math.round(result - baseAmount));
          const interests = resultsWithLabels
            .sort((a, b) => a.data.total.total.interestPayment - b.data.total.total.interestPayment)
            .map((result, index) => {
              const foundLabel = this.labelInterests.find(label => label.id === result.labelId);
              const labelHasRiskClassReduction = foundLabel && foundLabel.hasAutoRiskClassReduction;

              return {
                riskClassReduction: labelHasRiskClassReduction,
                category: `verstrekker${index}`,
                val: result.data.total.total.averageInterest,
                termInterestRate: true,
                labelName: Math.round(baseAmount + extraAmountForCategory[index]),
                baseAmountDifference: Math.round(extraAmountForCategory[index]),
                labelId: result.labelId,
              };
            });

          const categories = interests.map(interest => interest.category);

          this.setOverviewInterestPaymentComparisonData(
              extraAmountForCategory,
              interests,
              categories,
              baseAmount,
            );

          this.labelInterestsLoading = false;
        }
      })
      .catch((err) => { console.log(err); });
  }

    @action checkempty(resultsWithLabels) {
      this.dataIsEmpty = resultsWithLabels.some(x => _.isEmpty(x.data));
      return this.dataIsEmpty;
    }

  /* Helper method for getOverviewInterestPaymentComparisonData */
  @action sortInterestPaymentComparisonData(results) {
    return results.map(result => result.data.total.total.interestPayment)
      .sort((a, b) => a - b);
  }

  /* Helper method for getOverviewInterestPaymentComparisonData */
  @action setOverviewInterestPaymentComparisonData(extraAmountForCategory,
                                                   interests,
                                                   categories,
                                                   baseAmount
  ) {
    /* Create reference to overviewInterestPaymentComparisonData */
    const data = this.overviewInterestPaymentComparisonData;
    data.extraAmountForCategory = extraAmountForCategory;
    data.interests = interests;
    data.categories = categories;
    data.baseAmount = baseAmount;
  }

  /* Helper for getOverviewInterestPaymentComparisonData */
  @action setInterestPaymentComparisonLabels(total, frt) {
    const labels = this.labelInterests.slice();
    const fixedRateTermIndex = frt - 1;
    /* Get sorted labels with 20 rvp */
    return this.sortLabelInterests(labels, fixedRateTermIndex, 'averageInterest')
      .filter((label, index) => index < total)
      .map(label => label.id);
  }

  @action getComparisonDataWithDebounce(frt) {
    this.getOverviewInterestPaymentComparisonData(frt);
  }

  @action cancelRequest() {
    if (_.has(this.abortController, 'abort')) {
      /* Always clear pending request. */
      this.abortController.abort();
    }

    this.abortController = new AbortController();
  }

  @action toggleNHG(e) {
    this.overviewForm.nhg = e.target.checked ? 1 : 0;
    this.getInterestAndSortTable();
  }

  @action setOverviewForm(e) {
    const value = numeral(e.target.value).value();
    this.overviewForm[e.target.name] = Number(value);
    this.getInterestAndSortTable();
  }

  @action getNewLabelInterests(param) {
    const labelIsAvailable = this.labelInterests.some(labelInterest => labelInterest.interests[param - 1].averageInterest > 0);

    if (!labelIsAvailable) {
      this.getInterestForFrt(param).then((data) => {
        this.labelInterests = this.addNewLabelInterests(this.labelInterests, data);

        this.sortLabelInterestsByTermInterest(this.firstOverviewTableColumnFixedRateTerm);
        this.sortLabelInterestsByTermInterest(this.secondOverviewTableColumnFixedRateTerm);

        this.labelInterestsLoading = false;
        this.getComparisonDataWithDebounce(this.secondOverviewTableColumnFixedRateTerm);
        this.calculateInterestSavings();
      });
    }
  }

  @action getInterestAndSortTable() {
    if (!_.isEmpty(this.setBaseFormData())) {
      Promise.all([
        this.getInterestForFrt(20),
        this.getInterestForFrt(1),
        this.getInterestForFrt(5),
        this.getInterestForFrt(10),
        this.getInterestForFrt(15),
        this.getInterestForFrt(30),
        this.getInterestForFrt(25),
      ])
        .then((res) => {
          /* Equalize after first request to overview/interest */
          this.labelInterests = [];
          this.labelInterests = this.equalizeLabelInterests(res[0]);
          this.labelInterests = this.addNewLabelInterests(this.labelInterests, res[1]);
          this.labelInterests = this.addNewLabelInterests(this.labelInterests, res[2]);
          this.labelInterests = this.addNewLabelInterests(this.labelInterests, res[3]);
          this.labelInterests = this.addNewLabelInterests(this.labelInterests, res[4]);
          this.labelInterests = this.addNewLabelInterests(this.labelInterests, res[5]);
          this.labelInterests = this.addNewLabelInterests(this.labelInterests, res[6]);

          this.sortLabelInterestsByTermInterest(this.firstOverviewTableColumnFixedRateTerm);
          this.sortLabelInterestsByTermInterest(this.secondOverviewTableColumnFixedRateTerm);

          this.labelInterestsLoading = false;
          this.getComparisonDataWithDebounce(this.secondOverviewTableColumnFixedRateTerm);
          return this.calculateInterestSavings();
        });
    }
  }

  @action setBaseFormData() {
    let baseFormData = {};
    const { houseValue, mortgage, mortgageType, nhg } = { ...this.overviewForm };

    if (houseValue && mortgage > 4999 && mortgageType && nhg !== undefined) {
      baseFormData = { ...this.overviewForm };
    }

    /* If mortgage is lower than 5000 set errorMessage. */
    if (mortgage < 5000) {
      this.overviewInterestsErrorMessage = 'Gewenste hypotheek bedrag mag niet lager zijn dan €5.000,-';
    }

    return baseFormData;
  }

  @action toggleShowAllProviders() {
    this.showAllProviders = !this.showAllProviders;
  }

  @action getFixedRateTermIndex(fixedRateTerm) {
    return allFixedRateTerms.indexOf(fixedRateTerm);
  }

  @computed get allFixedRateTerms() {
    return allFixedRateTerms;
  }

  /**
   *
   * @param fixedRateTerm
   */
  @action sortLabelInterestsByInitialInterest(fixedRateTerm) {
    const labels = this.labelInterests.slice();
    const fixedRateTermIndex = this.getFixedRateTermIndex(fixedRateTerm);

    if (this.sortedOverviewTableColumn !== `initialInterest-${fixedRateTerm}`) {
      this.sortedOverviewTableColumn = `initialInterest-${fixedRateTerm}`;

      this.labelInterests = this.sortLabelInterests(labels, fixedRateTermIndex, 'initialInterest');
    }
  }

  /**
   *
   * @param fixedRateTerm
   */
  @action sortLabelInterestsByTermInterest(fixedRateTerm) {
    const labels = this.labelInterests.slice();
    const fixedRateTermIndex = this.getFixedRateTermIndex(fixedRateTerm);

    if (this.sortedOverviewTableColumn !== `averageInterest-${fixedRateTerm}`) {
      this.sortedOverviewTableColumn = `averageInterest-${fixedRateTerm}`;

      this.labelInterests = this.sortLabelInterests(labels, fixedRateTermIndex, 'averageInterest');
    }
  }

  @action sortLabelInterests(labels, fixedRateTermIndex, interestType) {
    return labels.sort((a, b) => {
      let sortedRow;
      if (a.interests[fixedRateTermIndex][interestType] === 0) {
        sortedRow = 1;
      } else if (b.interests[fixedRateTermIndex][interestType] === 0) {
        sortedRow = -1;
      } else if (a.interests.length > fixedRateTermIndex && b.interests.length > fixedRateTermIndex) {
        sortedRow = a.interests[fixedRateTermIndex][interestType] - b.interests[fixedRateTermIndex][interestType];
      } else if (a.interests.length <= fixedRateTermIndex) {
        sortedRow = 1;
      } else if (b.interests.length <= fixedRateTermIndex) {
        sortedRow = -1;
      }

      return sortedRow;
    });
  }

  @action sortLabelInterestsByTermInterestAvailable() {
    if (this.sortedOverviewTableColumn !== 'averageInterestAvailable') {
      /* Sort by second column */
      this.sortLabelInterestsByTermInterest(this.secondOverviewTableColumnFixedRateTerm);
      this.sortedOverviewTableColumn = 'averageInterestAvailable';

      /* Get all labelInterests with hasAutoRiskClassReduction property */
      const allLabelsInterestsWithHasAutoRiskClassReduction = this.labelInterests.filter(labelInterest => (
        labelInterest.hasAutoRiskClassReduction
      ));

      const allLabelsInterestsWithoutHasAutoRiskClassReduction = this.labelInterests
        .filter(labelInterest => labelInterest.hasAutoRiskClassReduction === false);

      this.labelInterests = [...allLabelsInterestsWithHasAutoRiskClassReduction,
        ...allLabelsInterestsWithoutHasAutoRiskClassReduction];
    }
  }

  @action setFirstOverviewTableColumnFixedRateTerm(firstOverviewTableColumnFixedRateTerm) {
    this.firstOverviewTableColumnFixedRateTerm = firstOverviewTableColumnFixedRateTerm;
  }

  @action setSecondOverviewTableColumnFixedRateTerm(secondOverviewTableColumnFixedRateTerm) {
    this.secondOverviewTableColumnFixedRateTerm = secondOverviewTableColumnFixedRateTerm;
  }

  @action setOpenSortingDropdown(order) {
    if (this.openSortingDropdown === order) {
      this.openSortingDropdown = '';
    } else {
      this.openSortingDropdown = order;
    }
  }

  @action closeOpenSortingDropdown() {
    this.openSortingDropdown = '';
  }

  @computed get showNhgAlertInfo() {
    return this.overviewForm.nhg === 1;
  }

  @computed get barWidth() {
    let barWidth = 90;

    // Make the chart width smaller according to the screen width
    if (window.innerWidth < 850) {
      barWidth = 70;
    }

    if (window.innerWidth < 650) {
      barWidth = 55;
    }

    return barWidth;
  }
}

const OverviewData = new OverviewStore();

export default OverviewData;
